import * as React from "react"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgBarChartSquarePlus } from "@digits-shared/components/SVGIcons/line/BarChartSquarePlus.svg"
import { SvgUserPlus01 } from "@digits-shared/components/SVGIcons/line/UserPlus01.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { HeaderActions } from "@digits-shared/DesignSystem/Containers/Headers/HeaderActions"
import { HeaderButtons } from "@digits-shared/DesignSystem/Containers/Headers/HeaderButtons"
import { DigitsLinkButton } from "@digits-shared/DesignSystem/LinkButton"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { RequiresCreate, RequiresUpdate } from "src/frontend/components/Shared/Permissions/Requires"
import { useOperatorBillingPlan } from "src/frontend/components/Shared/PLG/useOperatorBillingPlan"
import { usePortalDispatch } from "src/frontend/components/Shared/Portals/State/portalStore"
import { useIsEditLayoutActive } from "src/frontend/components/Shared/Portals/State/useIsEditLayoutActive"
import { Desktop } from "src/frontend/components/Shared/Responsive/Desktop"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import {
  FrontendPermissionModule,
  FrontendPermissionSource,
} from "src/frontend/session/permissionModule"
import { PeriodSelectorNav } from "src/shared/components/PeriodSelector/PeriodSelectorNav"

const ToggleContainer = styled.div`
  position: relative;
`

const EditIcon = styled(SvgBarChartSquarePlus)`
  width: 16px;
  height: 16px;
  margin-right: 6px;
  ${svgPathStyles(colors.secondary, 1.5)};
`

const AddIcon = styled(SvgUserPlus01)`
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.secondary, 2)};
`

export const LayoutHeader: React.FC = () => (
  <HeaderActions>
    <PeriodSelectorNav />

    <HeaderButtons>
      <InviteClientButton />
      <Desktop>
        <EditLayoutToggle />
      </Desktop>
    </HeaderButtons>
  </HeaderActions>
)

const EditLayoutToggle: React.FC = () => {
  const generatePath = useFrontendPathGenerator()
  const { currentLegalEntity } = useSession<FrontendSession>()
  const isEditing = useIsEditLayoutActive()

  return (
    <RequiresUpdate
      source={FrontendPermissionSource.LegalEntity}
      module={FrontendPermissionModule.Layouts}
    >
      <ToggleContainer>
        {isEditing ? (
          <DigitsLinkButton
            width={124}
            to={generatePath(routes.legalEntityHome, { leSlug: currentLegalEntity?.slug })}
          >
            Done
          </DigitsLinkButton>
        ) : (
          <DigitsLinkButton
            size="medium"
            $variant="secondary-white"
            to={generatePath(routes.legalEntityHomeEdit, {
              leSlug: currentLegalEntity?.slug,
            })}
          >
            <EditIcon />
            Edit
          </DigitsLinkButton>
        )}
      </ToggleContainer>
    </RequiresUpdate>
  )
}

const InviteClientButton: React.FC = () => {
  const { currentLegalEntity, currentOrganization } = useSession<FrontendSession>()
  const generatePath = useFrontendPathGenerator()
  const { history } = useRouter()
  const operatorBillingPlan = useOperatorBillingPlan()
  const portalDispatch = usePortalDispatch()

  const onClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      if (!currentOrganization) return

      // The history.replace below this conditional pops a modal that is specifically designed for inviting someone to
      // the organization as a managed operator. However, when a PLG user invites a team member, the invitation should
      // result in assigning them as an organization admin. Currently, this functionality is only supported through the
      // Invite modal located in the Team Members tab within Settings. As a temporary solution, Product has requested
      // the Invite Colleague button on the dashboard be a deep link to the Invite modal in Settings.
      if (operatorBillingPlan.hasBillingPlan) {
        history.push(generatePath(routes.addTeamMember, { leSlug: currentLegalEntity.slug }))
        return
      }

      portalDispatch({
        type: "setShowInviteClientsModal",
        show: true,
      })
    },
    [
      currentLegalEntity.slug,
      currentOrganization,
      generatePath,
      history,
      operatorBillingPlan.hasBillingPlan,
      portalDispatch,
    ]
  )

  return (
    <RequiresCreate
      source={FrontendPermissionSource.LegalEntity}
      module={FrontendPermissionModule.Invitations}
      allowDev={false}
    >
      <DigitsButton size="medium" $variant="secondary-white" onClick={onClick}>
        <AddIcon />
        Add User
      </DigitsButton>
    </RequiresCreate>
  )
}
